import { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Drawer,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ReactComponent as DropdownIcon } from "../../asserts/logo/dropdown.svg";
import { TRUST_ATTRIBUTES, trustAttributeLabel } from "../../config/trustAttributes";

const ATTRIBUTES = TRUST_ATTRIBUTES;
const SEVERITIES = ["Critical", "High", "Medium", "Low"];

const DIRECTION_OPTIONS = [
  { value: "gte", label: "≥ (greater or equal)" },
  { value: "lte", label: "≤ (less or equal)" },
  { value: "lt", label: "< (less than)" },
  { value: "eq", label: "= (equal)" },
];

const EMPTY = {
  catalogId: "",
  metricName: "",
  description: "",
  trustAttribute: "Reliability",
  riskId: "",
  direction: "gte",
  threshold: "0.80",
  unit: "fraction",
  severity: "High",
  reference: "",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "4px",
    bgcolor: "#fff",
    fontSize: "16px",
    "& fieldset": { borderColor: "#707070" },
    "&:hover fieldset": { borderColor: "#98A2B3" },
    "&.Mui-focused fieldset": { borderColor: "#555555", borderWidth: 1 },
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#707070",
    opacity: 1,
  },
};

const selectSx = {
  borderRadius: "4px",
  fontSize: "16px",
  color: "#707070",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#707070" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#555555" },
};

function RequiredMark() {
  return (
    <Typography component="span" sx={{ color: "#DE350B", ml: 0.25, fontWeight: 700 }}>
      *
    </Typography>
  );
}

function FieldLabel({ children, required }) {
  return (
    <Typography
      sx={{
        fontFamily: "Lato, sans-serif",
        fontWeight: 700,
        fontStyle: "normal",
        fontSize: "16px",
        lineHeight: "24px",
        letterSpacing: 0,
        color: "#707070",
        mb: 0.75,
      }}
    >
      {required ? <RequiredMark /> : null}
      {children}
    </Typography>
  );
}

function HelperText({ children }) {
  return (
    <Typography
      sx={{
        fontFamily: "Lato, sans-serif",
        fontWeight: 400,
        fontStyle: "normal",
        fontSize: "12px",
        lineHeight: "18px",
        letterSpacing: 0,
        color: "#707070",
        mt: 0.75,
      }}
    >
      {children}
    </Typography>
  );
}

function formatThreshold(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? String(n) : null;
}

function thresholdFromCatalog(catalogEntry) {
  return {
    threshold: formatThreshold(catalogEntry?.threshold) || EMPTY.threshold,
    direction: catalogEntry?.direction || "gte",
    unit: catalogEntry?.unit || "fraction",
  };
}

/**
 * Add a Derived metric — pick from product catalogue or invent a custom one.
 */
export default function AddMetricDialog({
  open,
  onClose,
  onSave,
  risks = [],
  catalog = [],
  metrics = [],
}) {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    const first = risks[0] || null;
    setForm({
      ...EMPTY,
      riskId: first?.id || "",
      trustAttribute: first?.attribute || "Reliability",
      severity: first?.severity || "High",
    });
  }, [open, risks]);

  const selectedCatalog = useMemo(
    () => catalog.find((c) => c.metric_id === form.catalogId) || null,
    [catalog, form.catalogId]
  );

  const risksForAttribute = useMemo(() => {
    const attr = form.trustAttribute;
    const all = risks || [];
    if (!attr) return all;
    return all.filter((r) => r.attribute === attr);
  }, [risks, form.trustAttribute]);

  // Keep linked risk aligned with the selected trust attribute
  useEffect(() => {
    if (!form.riskId) {
      if (risksForAttribute[0]?.id) {
        setForm((f) => ({
          ...f,
          riskId: risksForAttribute[0].id,
          severity: risksForAttribute[0].severity || f.severity,
        }));
      }
      return;
    }
    const stillValid = risksForAttribute.some((r) => r.id === form.riskId);
    if (!stillValid) {
      const next = risksForAttribute[0] || null;
      setForm((f) => ({
        ...f,
        riskId: next?.id || "",
        severity: next?.severity || f.severity,
      }));
    }
  }, [risksForAttribute, form.riskId]);

  const usedCatalogIdsForScope = useMemo(() => {
    const ids = new Set();
    for (const m of metrics || []) {
      if (m.riskId !== form.riskId || !m.metricId) continue;
      const mAttr = m.trustAttribute || m.attribute || "";
      if (mAttr !== form.trustAttribute) continue;
      ids.add(m.metricId);
    }
    return ids;
  }, [metrics, form.riskId, form.trustAttribute]);

  const isDuplicateOnScope = useMemo(() => {
    if (!form.riskId || !form.trustAttribute) return false;
    if (form.catalogId && usedCatalogIdsForScope.has(form.catalogId)) return true;
    const name = form.metricName.trim().toLowerCase();
    if (!name) return false;
    return (metrics || []).some((m) => {
      if (m.riskId !== form.riskId || m.metricId) return false;
      const mAttr = m.trustAttribute || m.attribute || "";
      if (mAttr !== form.trustAttribute) return false;
      return (
        String(m.metricName || "")
          .trim()
          .toLowerCase() === name
      );
    });
  }, [
    form.riskId,
    form.trustAttribute,
    form.catalogId,
    form.metricName,
    metrics,
    usedCatalogIdsForScope,
  ]);

  const canSave =
    Boolean(form.metricName.trim()) &&
    Boolean(form.riskId) &&
    Boolean(form.trustAttribute) &&
    Boolean(form.severity) &&
    Number.isFinite(Number(form.threshold)) &&
    !isDuplicateOnScope;

  const handle = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const metricAttr = (m) => m.trustAttribute || m.attribute || "";

  const usedOnScope = (riskId, trustAttribute, catalogId) =>
    (metrics || []).some(
      (m) =>
        m.riskId === riskId &&
        m.metricId === catalogId &&
        metricAttr(m) === trustAttribute
    );

  const handleAttributeChange = (e) => {
    const trustAttribute = e.target.value;
    setForm((f) => {
      const nextRisks = (risks || []).filter((r) => r.attribute === trustAttribute);
      const stillValid = nextRisks.some((r) => r.id === f.riskId);
      const pick = stillValid ? f.riskId : nextRisks[0]?.id || "";
      const linked = (risks || []).find((r) => r.id === pick);
      const next = {
        ...f,
        trustAttribute,
        riskId: pick,
        severity: linked?.severity || f.severity,
      };
      if (f.catalogId && usedOnScope(pick, trustAttribute, f.catalogId)) {
        next.catalogId = "";
      }
      return next;
    });
  };

  const handleRiskChange = (e) => {
    const riskId = e.target.value;
    const linked = (risks || []).find((r) => r.id === riskId);
    setForm((f) => {
      const trustAttribute = linked?.attribute || f.trustAttribute;
      const next = {
        ...f,
        riskId,
        trustAttribute,
        severity: linked?.severity || f.severity,
      };
      if (f.catalogId && usedOnScope(riskId, trustAttribute, f.catalogId)) {
        next.catalogId = "";
      }
      return next;
    });
  };

  const handleCatalogChange = (_, value) => {
    setForm((f) => {
      if (!value) {
        return {
          ...f,
          catalogId: "",
        };
      }
      const sug = thresholdFromCatalog(value);
      return {
        ...f,
        catalogId: value.metric_id,
        metricName: value.metric_name || f.metricName,
        // Always replace description with the newly selected catalogue metric's text
        description: value.description || "",
        threshold: sug.threshold,
        direction: sug.direction,
        unit: sug.unit,
      };
    });
  };

  const handleSave = async () => {
    if (!canSave) return;
    const threshold = Number(form.threshold);
    if (!Number.isFinite(threshold)) return;
    try {
      setSubmitting(true);
      await onSave({
        risk_id: form.riskId,
        metric_id: form.catalogId || null,
        metric_name: form.metricName.trim(),
        description: form.description.trim(),
        trust_attribute: form.trustAttribute,
        threshold,
        direction: form.direction || "gte",
        unit: form.unit || "fraction",
        severity: form.severity,
        reference_source: form.reference.trim(),
        source: "Defined",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 480, md: 520 },
          maxWidth: "100vw",
          boxShadow: "-8px 0 32px rgba(16, 24, 40, 0.14)",
        },
      }}
      slotProps={{
        backdrop: {
          sx: { bgcolor: "rgba(52, 64, 84, 0.45)" },
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: "#fff" }}>
        <Box sx={{ px: 3, pt: 3, pb: 2, borderBottom: "1px solid #EAECF0" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
            <Typography
              sx={{
                fontFamily: "Lato, sans-serif",
                fontWeight: 700,
                fontStyle: "normal",
                fontSize: "16px",
                lineHeight: "24px",
                letterSpacing: 0,
                color: "#555555",
              }}
            >
              Add derived metric
            </Typography>
            <IconButton
              size="small"
              onClick={onClose}
              aria-label="Close"
              sx={{ color: "#555555", mt: -0.5, mr: -0.5 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography
            sx={{
              fontFamily: "Lato, sans-serif",
              fontWeight: 400,
              fontStyle: "normal",
              fontSize: "14px",
              lineHeight: "24px",
              letterSpacing: 0,
              color: "#555555",
              mt: 1,
              pr: 2,
            }}
          >
            Derived metrics extend the canonical set with measurements specific to your business context. The table behind stays visible — you can copy details from an existing row.
          </Typography>
        </Box>

        <Box sx={{ flex: 1, overflow: "auto", px: 3, py: 2.5 }}>
          <Stack spacing={2.5}>
            <Box>
              <FieldLabel>Catalogue metric (optional)</FieldLabel>
              <Autocomplete
                options={catalog}
                value={selectedCatalog}
                onChange={handleCatalogChange}
                getOptionLabel={(c) => c?.metric_name || ""}
                isOptionEqualToValue={(a, b) => a?.metric_id === b?.metric_id}
                getOptionDisabled={(c) => usedCatalogIdsForScope.has(c.metric_id)}
                filterOptions={(options, { inputValue }) => {
                  const q = inputValue.trim().toLowerCase();
                  if (!q) return options;
                  return options.filter((c) => {
                    const fams = Array.isArray(c.families)
                      ? c.families.join(" ")
                      : c.family || "";
                    const hay = `${c.metric_name || ""} ${c.metric_id || ""} ${fams} ${
                      c.description || ""
                    }`.toLowerCase();
                    return hay.includes(q);
                  });
                }}
                clearOnEscape
                renderOption={(props, option) => {
                  const used = usedCatalogIdsForScope.has(option.metric_id);
                  const famLabel = Array.isArray(option.families)
                    ? option.families.join(", ")
                    : option.family || "";
                  return (
                    <Box component="li" {...props} key={option.metric_id}>
                      <Box sx={{ py: 0.25 }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#101828" }}>
                          {option.metric_name}
                          {used ? " (already on this risk + attribute)" : ""}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: "#667085" }}>
                          {famLabel}
                          {option.metric_id ? ` · ${option.metric_id}` : ""}
                          {option.threshold != null && Number.isFinite(Number(option.threshold))
                            ? ` · threshold ${option.threshold}`
                            : ""}
                        </Typography>
                      </Box>
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Search catalogue (name or id)…"
                    sx={fieldSx}
                  />
                )}
              />
              <HelperText>
                Leave empty to invent a custom metric. Selecting a catalogue metric prefills name,
                description, and threshold (replaced when you pick another).
              </HelperText>
            </Box>

            <Box>
              <FieldLabel required>Metric name</FieldLabel>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Demographic parity gap"
                value={form.metricName}
                onChange={handle("metricName")}
                sx={fieldSx}
              />
              <HelperText>
                Short, action-oriented. This is how the metric appears in dashboards and reports.
              </HelperText>
              {isDuplicateOnScope ? (
                <Typography sx={{ mt: 0.75, fontSize: 13, color: "#DE350B" }}>
                  This metric is already linked to this risk under the selected trust attribute.
                </Typography>
              ) : null}
            </Box>

            <Box>
              <FieldLabel>Description</FieldLabel>
              <TextField
                fullWidth
                multiline
                minRows={4}
                placeholder="Describe what this metric captures and how it's calculated."
                value={form.description}
                onChange={handle("description")}
                sx={fieldSx}
              />
              <HelperText>what it measures, and how</HelperText>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 1.5,
                alignItems: "start",
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <FieldLabel required>Trust attribute</FieldLabel>
                <FormControl fullWidth size="small">
                  <Select IconComponent={DropdownIcon} value={form.trustAttribute} onChange={handleAttributeChange} sx={selectSx}>
                    {ATTRIBUTES.map((a) => (
                      <MenuItem key={a} value={a}>
                        {trustAttributeLabel(a)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <FieldLabel required>Linked risk</FieldLabel>
                <FormControl fullWidth size="small">
                  <Select IconComponent={DropdownIcon}
                    value={form.riskId}
                    onChange={handleRiskChange}
                    displayEmpty
                    sx={selectSx}
                  >
                    {risksForAttribute.length === 0 ? (
                      <MenuItem value="" disabled>
                        No risks for this trust attribute
                      </MenuItem>
                    ) : (
                      risksForAttribute.map((r) => (
                        <MenuItem key={r.id} value={r.id}>
                          {r.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            <Box>
              <FieldLabel required>Threshold</FieldLabel>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 1.5,
                  alignItems: "start",
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <FieldLabel>Operator</FieldLabel>
                  <FormControl fullWidth size="small">
                    <Select IconComponent={DropdownIcon}
                      value={form.direction || "gte"}
                      onChange={handle("direction")}
                      sx={selectSx}
                    >
                      {DIRECTION_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <FieldLabel>Value</FieldLabel>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    inputProps={{ step: "0.01" }}
                    value={form.threshold}
                    onChange={handle("threshold")}
                    sx={fieldSx}
                  />
                </Box>
              </Box>
            </Box>

            <Box>
              <FieldLabel required>Severity</FieldLabel>
              <FormControl fullWidth size="small">
                <Select IconComponent={DropdownIcon} value={form.severity} onChange={handle("severity")} sx={selectSx}>
                  {SEVERITIES.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box>
              <FieldLabel>Reference / Source</FieldLabel>
              <TextField
                fullWidth
                size="small"
                placeholder="https://... or internal doc ID"
                value={form.reference}
                onChange={handle("reference")}
                sx={fieldSx}
              />
              <HelperText>link to policy, regulation or incident</HelperText>
            </Box>
          </Stack>
        </Box>

        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid #EAECF0",
            display: "flex",
            alignItems: { xs: "stretch", sm: "center" },
            justifyContent: "space-between",
            gap: 2,
            flexDirection: { xs: "column", sm: "row" },
            bgcolor: "#fff",
          }}
        >
          <Typography sx={{ fontSize: 12, color: "#667085", lineHeight: 1.4, flex: 1 }}>
            Derived metric · you can edit or delete it any time
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end", flexShrink: 0 }}>
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                color: "#005BF0",
                borderColor: "#1A6EF5",
                borderRadius: "4px",
                px: 2,
                "&:hover": { borderColor: "#1251c0", bgcolor: "rgba(26, 110, 245, 0.04)" },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={submitting || !canSave}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                bgcolor: "#005BF0",
                borderRadius: "4px",
                px: 2,
                boxShadow: "none",
                "&:hover": { bgcolor: "#1251c0", boxShadow: "none" },
                "&.Mui-disabled": { bgcolor: "#005BF0", color: "#fff" },
              }}
            >
              Save &amp; add metric
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
}
